/**
 * iter95 — Onglet "Leads Léa" (page Demandes RDV).
 *
 * Conversations du widget IA conservées côté serveur :
 *   nouveau → en_cours → abandonne | converti
 * Permet de relancer les clients qui ont abandonné en cours de route
 * (coordonnées affichées dès qu'elles sont connues) et de suivre le taux
 * de conversion de Léa.
 */
import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, Phone, Bot, CheckCircle2, MessageSquare, Eye,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUT_STYLE = {
  nouveau: "bg-blue-50 text-blue-700 border-blue-200",
  en_cours: "bg-amber-50 text-amber-700 border-amber-200",
  abandonne: "bg-red-50 text-red-700 border-red-200",
  converti: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUT_LABEL = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  abandonne: "Abandonné",
  converti: "Converti ✓",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });
}

export default function LeadsLeaTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null); // conversation complète

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ia-leads", { params: filter ? { statut: filter } : {} });
      setItems(data.items || []);
      setStats(data.stats || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/ia-leads/${id}`);
      setDetail(data);
    } catch {
      toast.error("Conversation introuvable");
    }
  };

  const marquerTraite = async (lead) => {
    try {
      await api.patch(`/ia-leads/${lead.id}`, { traite: !lead.traite });
      toast.success(lead.traite ? "Lead remis à traiter" : "Lead marqué traité");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="ia-leads-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          {["nouveau", "en_cours", "abandonne", "converti"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(filter === s ? "" : s)}
              className={`p-3 rounded-lg border text-left transition-all ${
                filter === s ? "ring-2 ring-[#52a675] " : ""
              }${STATUT_STYLE[s]}`}
              data-testid={`ia-leads-filter-${s}`}
            >
              <div className="text-xl font-bold">{stats[s]}</div>
              <div className="text-[11px] font-medium">{STATUT_LABEL[s]}</div>
            </button>
          ))}
          <div className="p-3 rounded-lg border border-gray-200 bg-white" data-testid="ia-leads-taux">
            <div className="text-xl font-bold text-gray-900">{stats.taux_conversion}%</div>
            <div className="text-[11px] text-gray-500 font-medium">Taux de conversion</div>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && items.length === 0 && (
        <Card className="p-10 text-center text-gray-400 border-gray-200">
          <Bot className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          Aucune conversation {filter ? `« ${STATUT_LABEL[filter]} »` : "Léa"} pour l'instant.
        </Card>
      )}

      <div className="space-y-2">
        {items.map((l) => (
          <Card key={l.id} className="p-3.5 border-gray-200" data-testid={`ia-lead-${l.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant="outline" className={STATUT_STYLE[l.statut] || ""}>
                    {STATUT_LABEL[l.statut] || l.statut}
                  </Badge>
                  {l.traite && (
                    <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Traité
                    </Badge>
                  )}
                  <span className="text-sm font-semibold text-gray-900">
                    {(l.prenom || l.nom) ? `${l.prenom} ${l.nom}`.trim() : "Identité inconnue"}
                  </span>
                  <span className="text-xs text-gray-400">· {fmtDateTime(l.last_activity_at)}</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap text-xs text-gray-700">
                  {l.telephone ? (
                    <a href={`tel:${l.telephone}`} className="flex items-center gap-1 font-mono hover:text-[#52a675]" data-testid={`ia-lead-tel-${l.id}`}>
                      <Phone className="w-3 h-3 text-gray-400" /> {l.telephone}
                    </a>
                  ) : (
                    <span className="text-gray-400 italic">téléphone non collecté</span>
                  )}
                  {l.type_label && <span>🔧 {l.type_label}</span>}
                  {l.date_souhaitee && <span>📅 {l.date_souhaitee}</span>}
                  <span className="text-gray-400 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> {l.nb_messages} messages
                  </span>
                </div>
                {l.dernier_message && (
                  <div className="text-xs text-gray-500 mt-1.5 italic truncate">
                    « {l.dernier_message} »
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openDetail(l.id)} data-testid={`ia-lead-voir-${l.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Conversation
                </Button>
                {l.statut === "abandonne" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => marquerTraite(l)}
                    className={l.traite ? "border-gray-300 text-gray-500" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}
                    data-testid={`ia-lead-traite-${l.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                    {l.traite ? "Remettre à traiter" : "Marquer traité"}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Dialog conversation complète */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ia-lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-[#52a675]" />
              Conversation Léa {detail?.prenom ? `· ${detail.prenom} ${detail.nom}` : ""}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                <span>Début : {fmtDateTime(detail.created_at)}</span>
                <span>Dernière activité : {fmtDateTime(detail.last_activity_at)}</span>
                {detail.rdv_id && <span className="text-emerald-700 font-medium">RDV créé ✓</span>}
              </div>
              <div className="space-y-1.5 mt-2">
                {(detail.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-3 py-1.5 rounded-xl text-xs ${
                      m.role === "user" ? "bg-[#52a675] text-white" : "bg-gray-100 text-gray-900"
                    }`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
